import type { SourceDataFirm } from "./sourceDataFirm";
import { NO_FIRM_SENTENCE, unresolvedReasonText } from "./unresolvedReasonText";

export type SourceDataOutcome<T> =
  | { kind: "loading" }
  | { kind: "data"; data: T }
  | { kind: "empty" }
  | { kind: "unknown"; sentence: string }
  | { kind: "error" };

export type SourceDataQueryState<R> = {
  status: "pending" | "error" | "success";
  data: R | undefined;
};

/**
 * One of the four outcomes (plus loading) for a source-data surface: a reason means unknown whatever the
 * data holds, a failed request means error, and an empty answer with no reason means empty.
 */
export function sourceDataOutcome<R extends { unresolvedReason: string | null }, T>(
  firm: SourceDataFirm,
  query: SourceDataQueryState<R>,
  select: (response: R) => T | null,
  isEmpty: (data: T) => boolean,
): SourceDataOutcome<T> {
  if (firm.kind === "none") return { kind: "unknown", sentence: NO_FIRM_SENTENCE };
  if (firm.kind === "pending") return { kind: "loading" };
  if (query.status === "error") return { kind: "error" };
  if (query.status === "pending" || query.data === undefined) return { kind: "loading" };

  const response = query.data;
  if (response.unresolvedReason !== null) {
    return { kind: "unknown", sentence: unresolvedReasonText(response.unresolvedReason) };
  }
  const data = select(response);
  if (data === null || isEmpty(data)) return { kind: "empty" };
  return { kind: "data", data };
}
